import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import 'aos/dist/aos.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Hero from '../components/sections/Hero';
import Services from '../components/sections/Services';
import Portfolio from '../components/sections/Portfolio';
import Testimonials from '../components/sections/Testimonials';
import CTA from '../components/sections/CTA';
import Contact from '../components/sections/Contact';
import useLocalPageData from '../hook/useLocalPageData';
import { Helmet } from 'react-helmet';
import { ThemeProvider as CustomThemeProvider, useTheme } from '../context/ThemeContext';

// Style global unique
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'Poppins', sans-serif;
    transition: background-color 0.3s ease, 
                color 0.3s ease, 
                border-color 0.3s ease, 
                box-shadow 0.3s ease;
    -webkit-tap-highlight-color: transparent; /* Supprime le highlight bleu sur mobile */
    -webkit-touch-callout: none; /* Désactive le menu contextuel sur iOS */
    -webkit-user-select: none; /* Empêche la sélection de texte sur WebKit */
    user-select: none; /* Standard syntax */
  }
  
  html, body {
    overflow-x: hidden;
    scroll-behavior: smooth;
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
  }
  
  a {
    text-decoration: none;
    color: inherit;
  }
  
  button {
    cursor: pointer;
    border: none;
    outline: none;
  }

  /* Permet la sélection de texte pour les paragraphes et autres contenus textuels */
  p, span, div, label {
    user-select: text;
  }
`;

// Composant pour la section de contenu local
const LocalPageSection = styled.section`
  padding: 10rem 5% 4rem;
  background-color: ${props => props.theme.colors.background};
`;

const LocalPageContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.text};
  
  span {
    color: ${props => props.theme.colors.accent};
  }
  
  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
`;

const HomePageWrapper = () => {
  const { theme } = useTheme();

  return (
    <ThemeProvider theme={theme}>
      <>
        <GlobalStyle />
        <HomePage />
      </>
    </ThemeProvider>
  );
};

const HomePage = () => {
  const { slug } = useParams(); // Si aucun slug, ce sera undefined
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // On charge les données locales uniquement si on a un slug
  const { data, loading, error } = useLocalPageData(slug);

  // Détermine s'il s'agit d'une page locale ou de la page d'accueil
  const isLocalPage = !!slug;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Affiche le chargement uniquement pour les pages locales
  if (isLocalPage && loading) return <div>Chargement...</div>;
  if (isLocalPage && error) return <div>{error}</div>;

  return (
    <>
      {/* Meta-tags pour SEO - conditionnels en fonction du type de page */}
      {isLocalPage ? (
        <Helmet>
          <title>{data.meta_title}</title>
          <meta name="description" content={data.meta_description} />
          <meta name="keywords" content={`${data.mots_cles_primaires}, ${data.mots_cles_secondaires}`} />
          <link rel="canonical" href={`https://rankeed.fr/${data.url_slug}`} />
          <meta property="og:title" content={data.meta_title} />
          <meta property="og:description" content={data.meta_description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`https://rankeed.fr/${data.url_slug}`} />
        </Helmet>
      ) : (
        <Helmet>
          <title>Rankeed - Création de sites web professionnels</title>
          <meta name="description" content="Agence web spécialisée dans la création de sites web professionnels et le développement sur mesure pour entreprises et professionnels." />
          <meta property="og:url" content={`https://rankeed.fr`} />
          <link rel="canonical" href="https:/rankeed.fr" />
        </Helmet>
      )}

      <Header
        isScrolled={isScrolled}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />

      {/* Contenu de la page d'accueil ou de la page locale */}
      <Hero />
      <Services />
      <Portfolio />
      <Testimonials />
      <CTA />
      <Contact />
      <Footer />
    </>
  );
};

// Exporte le composant avec le ThemeProvider
const AppWithTheme = () => {
  return (
    <CustomThemeProvider>
      <HomePageWrapper />
    </CustomThemeProvider>
  );
};

export default AppWithTheme;